import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import './characterBioDialog.css';

interface CharacterBioDialogProps {
  open: boolean;
  onClose: () => void;
}

const CharacterBioDialog: React.FC<CharacterBioDialogProps> = ({ open, onClose }) => {
  const { t } = useTranslation();
  const [currentBatch, setCurrentBatch] = useState(0);
  const [displayedText, setDisplayedText] = useState('');
  const [isAnimating, setIsAnimating] = useState(true);

  // Define text batches - you can customize these
  const textBatches = [
    t('characterBio.batch1'),
    t('characterBio.batch2'),
    t('characterBio.batch3'),
    t('characterBio.batch4'),
  ];

  const currentText = textBatches[currentBatch];

  // Character-by-character animation effect
  useEffect(() => {
    if (!isAnimating || !currentText) return;

    let currentIndex = 0;
    const animationInterval = setInterval(() => {
      if (currentIndex <= currentText.length) {
        setDisplayedText(currentText.substring(0, currentIndex));
        currentIndex++;
      } else {
        setIsAnimating(false);
        clearInterval(animationInterval);
      }
    }, 50); // Adjust speed here (milliseconds per character)

    return () => clearInterval(animationInterval);
  }, [currentText, isAnimating]);

  // Reset animation when batch changes
  useEffect(() => {
    setDisplayedText('');
    setIsAnimating(true);
  }, [currentBatch]);

  const handleTextBoxClick = () => {
    if (isAnimating) {
      // Skip animation and show full text
      setDisplayedText(currentText);
      setIsAnimating(false);
    } else {
      // Move to next batch
      if (currentBatch < textBatches.length - 1) {
        setCurrentBatch(currentBatch + 1);
      } else {
        // Close dialog when reaching the end
        onClose();
      }
    }
  };

  if (!open) return null;

  return (
    <div className="character-bio-overlay" onClick={onClose}>
      <div className="character-bio-container" onClick={(e) => e.stopPropagation()}>
        {/* Pixel art corner decorations */}
        <div className="pixel-corner top-left"></div>
        <div className="pixel-corner top-right"></div>
        <div className="pixel-corner bottom-left"></div>
        <div className="pixel-corner bottom-right"></div>

        {/* Text box */}
        <div className="textbox-frame">
          <button className="bio-close-button" aria-label="Close dialog" onClick={onClose}>x</button>
          <div className="textbox-title">
            <span className="title-text">About Me</span>
          </div>
          
          <div className="textbox-content" onClick={handleTextBoxClick}>
            <p className="animated-text">
              {displayedText}
              {isAnimating && <span className="text-cursor">▮</span>}
            </p>
          </div>

          {/* Navigation indicator */}
          <div className="textbox-footer">
            <span className="page-indicator">
              {currentBatch + 1} / {textBatches.length}
            </span>
            {isAnimating ? (
              <span className="action-hint">[Click to skip]</span>
            ) : (
              <span className="action-hint">
                {currentBatch < textBatches.length - 1 ? '[Click for more]' : '[Click to close]'}
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default CharacterBioDialog;
